import { Form, Modal, Select, Table, Typography } from "antd";
import { ColumnType } from "antd/es/table";
import { useEffect, useMemo, useState } from "react";
import { useCreateBank, useDeleteBank, useUpdateBank } from "../../api/bank";
import { ReactComponent as CheckSelectIcon } from "../../common/images/svg/CheckSelect.svg";
import { ReactComponent as CloseIcon } from "../../common/images/svg/CloseIcon.svg";
import { ReactComponent as PencilIcon } from "../../common/images/svg/PencilSimpleLine.svg";
import { ReactComponent as PlusIcon } from "../../common/images/svg/Plus.svg";
import { ReactComponent as ArrowIcon } from "../../common/images/svg/SuffixSelectIcon.svg";
import { ReactComponent as TrashIcon } from "../../common/images/svg/Trash.svg";
import { ReactComponent as ClearIcon } from "../../common/images/svg/XCircle.svg";
import CustomBreadCrumb from "../../components/breadCrumb/breadCrumb";
import { CustomButton } from "../../components/button/buttonCustom";
import { SearchCustom } from "../../components/inputs/search/searchCustom";
import ConfirmModal from "../../components/modal/confirmModal";
import NextPagination from "../../components/pagination/next";
import PrevPagination from "../../components/pagination/prev";
import { useNotification } from "../../components/providers/notification/useNotification";
import { setTitleHeader } from "../../store/use-title-header-store";
import { useBankManagement } from "../../utils/hooks/useBankManagement";
import { useMetadataCountries } from "../../utils/hooks/useMetadataCountries";
import useResponsive from "../../utils/hooks/useResponsive";
import { Row as RowTable } from "../components/interface";
import "./bank-management.css";
import FormBank from "./form-bank";

export type IFormBank = {
  name: string;
  swift: string;
  legalAddress: string;
  country: string;
};

const breadCrumb = [
  {
    name: "Portfolio",
    path: "/",
  },
  {
    name: "Banks",
    path: "/bank",
  },
];

const start = 0;
const end = 20;

export const ThemedBankManagement: React.FC<any> = () => {
  const { isTablet } = useResponsive();
  const [form] = Form.useForm<IFormBank>();
  const [actionBank, setActionBank] = useState<"ADD" | "EDIT" | null>(null);
  const [bankId, setBankId] = useState<number | null>(null);
  const [deleteBankId, setDeleteBankId] = useState<number | null>(null);
  const [params, setParams] = useState({ start, end });
  const [searchName, setSearchName] = useState<string>("");
  const [searchCountry, setSearchCountry] = useState<string>("");
  const [currentPage, setCurrentPage] = useState(1);
  const { open: openNotification } = useNotification();

  const { metadataCountries } = useMetadataCountries();
  const { dataBankManagement, refetch, isLoading } = useBankManagement(
    params,
    searchName,
    searchCountry
  );

  const { mutate: createBank, isLoading: isLoadingCreate } = useCreateBank({
    onSuccess: (response: { data: { message: string } }) => {
      if (openNotification) {
        openNotification?.({
          key: "add_bank_success",
          message: response.data.message || "Add bank successfully!",
          type: "success",
        });
      }
      refetch();
      setActionBank(null);
      setBankId(null);
      form.resetFields();
    },
    onError: (error: { message: string }) => {
      if (openNotification) {
        openNotification({
          key: "add_bank_failure",
          message: error?.message || "Add bank failed!",
          type: "error",
        });
      }
    },
  });

  const { mutate: updateBank, isLoading: isLoadingUpdate } = useUpdateBank({
    onSuccess: (response: { data: { message: string } }) => {
      if (openNotification) {
        openNotification?.({
          key: "update_bank_success",
          message: response.data.message || "Update bank successfully!",
          type: "success",
        });
      }
      refetch();
      setActionBank(null);
      setBankId(null);
      form.resetFields();
    },
    onError: (error: { message: string }) => {
      if (openNotification) {
        openNotification({
          key: "update_bank_failure",
          message: error?.message || "Update bank failed!",
          type: "error",
        });
      }
    },
  });

  const { mutate: deleteBank, isLoading: loadingDeleteBank } = useDeleteBank({
    onSuccess: (response: { data: { message: string } }) => {
      if (openNotification) {
        openNotification?.({
          key: "delete_bank_success",
          message: response.data.message || "Delete bank successfully!",
          type: "success",
        });
      }
      refetch();
      setDeleteBankId(null);
    },
    onError: (error: { message: string }) => {
      if (openNotification) {
        openNotification({
          key: "delete_bank_failure",
          message: error?.message || "Delete bank failed!",
          type: "error",
        });
      }
    },
  });

  const onSubmitWallet = (values: IFormBank) => {
    if (actionBank === "ADD") {
      createBank(values);
    } else {
      if (!bankId) return;
      updateBank({ id: bankId, ...values });
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    setParams({
      start: (page - 1) * dataBankManagement.data.pageSize || start,
      end: page * dataBankManagement.data.pageSize || end,
    });
  };

  const handleSearch = (value: string) => {
    setSearchName(value);
  };

  const handleChangeCountry = (value: string) => {
    setSearchCountry(value);
  };

  const handleCancel = () => {
    if (isLoadingUpdate || isLoadingCreate) return;
    setActionBank(null);
    setBankId(null);
    form.resetFields();
  };

  const handleCancelDelete = () => {
    if (loadingDeleteBank) return;
    setDeleteBankId(null);
  };

  const handleSubmitDelete = () => {
    deleteBank(deleteBankId);
  };

  const handleClickOnCell = (record: RowTable) => {
    return {
      onClick: () => {
        setActionBank("EDIT");
        setBankId(record.id);
      },
    };
  };

  const columns = useMemo((): ColumnType<RowTable>[] => {
    const columns: ColumnType<RowTable>[] = [
      {
        dataIndex: "id",
        title: <span className={"column-header"}>NO.</span>,
        render: (_value, _record, index) => {
          return (
            <Typography className="text-table">
              {(dataBankManagement.data.page - 1) *
                dataBankManagement.data.pageSize +
                index +
                1}
            </Typography>
          );
        },
        onCell: handleClickOnCell,
        width: "7.2%",
      },
      {
        dataIndex: "name",
        title: <span className={"column-header"}>NAME</span>,
        onCell: handleClickOnCell,
        render: (value) => {
          return <Typography className="text-table">{value}</Typography>;
        },
        width: "33.6%",
      },
      {
        dataIndex: "swift",
        title: <span className={"column-header"}>SWIFT</span>,
        onCell: handleClickOnCell,
        width: "10.8%",
      },
      {
        dataIndex: "legalAddress",
        title: <span className={"column-header"}>ADDRESS</span>,
        onCell: handleClickOnCell,
        render: (value) => {
          return <Typography className="text-table">{value}</Typography>;
        },
        width: "25.2%",
      },
      {
        dataIndex: "country",
        title: <span className={"column-header"}>COUNTRY</span>,
        onCell: handleClickOnCell,
        render: (value) => {
          return <Typography className="text-table">{value}</Typography>;
        },
        width: "15%",
      },
      {
        title: <div></div>,
        render: (record: RowTable) => (
          <div className="action-table">
            <TrashIcon
              className="icon-animation"
              onClick={() => setDeleteBankId(record.id)}
            />
            <PencilIcon
              className="icon-animation"
              onClick={() => {
                setActionBank("EDIT");
                setBankId(record.id);
              }}
            />
          </div>
        ),
      },
    ];
    return columns.filter(({ key }) => !key);
  }, [dataBankManagement]);

  useEffect(() => {
    setTitleHeader("Bank Management");
  }, []);

  return (
    <div className="bank-management-container">
      <CustomBreadCrumb breadCrumb={breadCrumb} showHome={true} />
      <div className="bank-management-header">
        <div className="bank-management-header-left">
          <SearchCustom
            value={searchName}
            placeholder="Search by name"
            onChange={handleSearch}
            onSubmit={handleSearch}
          />
          <Select
            showSearch
            clearIcon={<ClearIcon style={{ height: 12, width: 12 }} />}
            style={{ width: "150px" }}
            placeholder="All Countries"
            suffixIcon={searchCountry ? null : <ArrowIcon />}
            onChange={handleChangeCountry}
            allowClear
            optionLabelProp="label"
            filterOption={(input, option) => {
              const label = option?.label;
              if (typeof label === "string") {
                return label.toLowerCase().includes(input.toLowerCase());
              }
              return false;
            }}
          >
            {metadataCountries.map((item) => (
              <Select.Option
                key={item.value}
                value={item.value}
                label={item.label}
              >
                <span
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    width: "100%",
                    gap: 2,
                  }}
                >
                  <span
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      flex: 1,
                    }}
                  >
                    {item.label}
                  </span>
                  {searchCountry === item.value && (
                    <CheckSelectIcon style={{ flexShrink: 0 }} />
                  )}
                </span>
              </Select.Option>
            ))}
          </Select>
        </div>
        <CustomButton
          size={isTablet ? "small" : "medium"}
          text="Add Bank"
          icon={<PlusIcon />}
          onClick={() => {
            setActionBank("ADD");
            setBankId(null);
          }}
        />
      </div>
      <Table
        className={`custom-ant-table custom-ant-table-beneficiary ${
          isLoading ? "table-hidden-empty" : ""
        }`}
        rowKey="id"
        rowClassName={() => "custom-table-row"}
        style={{ tableLayout: "auto" }}
        columns={columns as any}
        dataSource={dataBankManagement.data.items}
        loading={isLoading}
        scroll={{ y: "calc(100vh - 295px)" }}
        pagination={{
          current: currentPage,
          total: dataBankManagement.data.total,
          pageSize: dataBankManagement.data.pageSize,
          onChange: handlePageChange,
          itemRender: (page, type, element) => {
            switch (type) {
              case "prev":
                return (
                  <PrevPagination
                    currentPage={currentPage}
                    setCurrentPage={(page) => handlePageChange(page)}
                  />
                );
              case "next":
                return (
                  <NextPagination
                    totalPage={dataBankManagement.data.totalPages}
                    currentPage={currentPage}
                    setCurrentPage={(page) => handlePageChange(page)}
                  />
                );
            }
            return element;
          },
        }}
      />
      <Modal
        open={!!actionBank}
        onCancel={handleCancel}
        footer={null}
        destroyOnClose
        maskClosable={false}
        title={
          <>
            <Typography
              style={{ fontSize: 20, fontWeight: 700, color: "#C2C6CE" }}
            >
              {actionBank === "ADD" ? "Add Bank" : "Edit Bank"}
            </Typography>
            <CloseIcon onClick={handleCancel} className="icon-animation" />
          </>
        }
        className="edit-wallet-modal"
      >
        <FormBank
          form={form}
          bankId={bankId}
          handleCancel={handleCancel}
          onSubmitWallet={onSubmitWallet}
          isLoading={isLoadingCreate || isLoadingUpdate}
        />
      </Modal>
      <ConfirmModal
        type="warning"
        title="Confirm Delete"
        description="Do you want to delete this record?"
        open={!!deleteBankId}
        onCancel={handleCancelDelete}
        onSubmit={handleSubmitDelete}
        isLoading={loadingDeleteBank}
      />
    </div>
  );
};
